import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { chatAPI } from '../services/api';

import websocketService from '../services/websocketService';
import {
  Box,
  Container,
  Typography,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Badge,
  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputBase,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  ArrowBack,
  MoreVert,
  Send,
  AttachFile,
  Image,
  Description,
  PictureAsPdf,
  Videocam,
  Phone,
  Search,
  Info,
  EmojiEmotions,
  Mic,
  Check,
  Done,
  DoneAll,
  Close
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContextNew';
import BrandLoader from '../components/BrandLoader';
import useResponsive from '../hooks/useResponsive';

const ChatConversation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = useParams(); // This will now be the provider ID
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { 
    isMobile: isResponsiveMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [messageStatuses, setMessageStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatInfo, setChatInfo] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const chatInfoRef = useRef(null); // ✅ Ref to access latest chatInfo in event handlers
  
  // Update ref whenever chatInfo changes
  useEffect(() => {
    chatInfoRef.current = chatInfo;
  }, [chatInfo]);

  // Fetch chat messages from API
  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if we already have the actual chat ID from navigation state
      let actualChatId = location.state?.provider?.actualChatId;
      let createChatResponse = null;
      
      // If not, create or get chat with provider ID
      if (!actualChatId) {
        createChatResponse = await chatAPI.createChat(chatId);
        
        if (createChatResponse.success) {
          actualChatId = createChatResponse.data.chatId;
        } else {
          setError(createChatResponse.error || 'Failed to create chat');
          return;
        }
      } else {
      }
      
      // Now get messages using the actual chat ID
      const response = await chatAPI.getChatMessages(actualChatId);
      
      if (response.success) {
        // Process messages to ensure proper timestamp format
        const processedMessages = (response.data.messages || []).map(msg => ({
          ...msg,
          timestamp: msg.timestamp || new Date().toISOString()
        }));
        setMessages(processedMessages);
        
        // Set provider info from navigation state or createChat response
        if (location.state?.provider) {
          const provider = location.state.provider;
          setChatInfo({
            id: chatId,
            actualChatId: actualChatId,
            name: provider.name || 'Provider',
            // Multiple image source options from location state
            photoURL: provider.photoURL,
            profilePhotoUrl: provider.profilePhotoUrl,
            profilePicture: provider.profilePicture,
            logoUrl: provider.logoUrl,
            businessImage: provider.businessImage,
            image: provider.image,
            avatar: provider.avatar, // Keep for backward compatibility
            isOnline: false,
            lastSeen: null,
            providerId: provider.id,
            businessType: provider.businessType
          });
        } else if (createChatResponse?.data?.provider) {
          const provider = createChatResponse.data.provider;
          setChatInfo({
            id: chatId, // Use provider ID for display
            actualChatId: actualChatId, // Store actual chat ID for sending messages
            name: provider.businessName || `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || 'Provider',
            // Multiple image source options from createChat response
            photoURL: provider.photoURL,
            profilePhotoUrl: provider.profilePhotoUrl,
            profilePicture: provider.profilePicture,
            logoUrl: provider.logoUrl,
            businessImage: provider.businessImage,
            image: provider.image,
            avatar: provider.logoUrl || provider.profilePhotoUrl, // Keep for backward compatibility
            isOnline: false,
            lastSeen: null,
            providerId: provider.id,
            businessType: provider.businessType
          });
        } else if (response.data.chat && response.data.chat.provider) {
          // Fallback: Use provider info from chat data
          const provider = response.data.chat.provider;
          setChatInfo({
            id: chatId,
            actualChatId: actualChatId,
            name: provider.businessName || `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || 'Provider',
            // Multiple image source options from chat data
            photoURL: provider.photoURL,
            profilePhotoUrl: provider.profilePhotoUrl,
            profilePicture: provider.profilePicture,
            logoUrl: provider.logoUrl,
            businessImage: provider.businessImage,
            image: provider.image,
            avatar: provider.logoUrl || provider.profilePhotoUrl, // Keep for backward compatibility
            isOnline: false,
            lastSeen: null,
            providerId: provider.id,
            businessType: provider.businessType
          });
        } else {
          // Final fallback
          setChatInfo({
            id: chatId,
            actualChatId: actualChatId,
            name: 'Provider',
            photoURL: null,
            profilePhotoUrl: null,
            profilePicture: null,
            logoUrl: null,
            businessImage: null,
            image: null,
            avatar: null,
            isOnline: false,
            lastSeen: null
          });
        }
      } else {
        setError('Failed to load messages');
      }
    } catch (error) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for a specific chat ID
  const fetchMessagesForChat = async (specificChatId) => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      try {
        const directResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/customer/chat/${specificChatId}/messages?t=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('customerToken') || localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!directResponse.ok) {
          throw new Error(`HTTP error! status: ${directResponse.status}`);
        }
        
        const responseData = await directResponse.json();
        
        response = {
          success: true,
          data: {
            messages: responseData.data.messages || [],
            chat: responseData.data.chat || null
          }
        };
      } catch (apiError) {
        throw apiError;
      }
      
      if (response.success) {
        // Process messages to ensure proper format and prevent duplicates
        const processedMessages = (response.data.messages || []).map(msg => {
          // Ensure proper sender format for customer app
          let sender = msg.sender;
          if (msg.senderType) {
            // If senderType exists, use it instead of sender
            sender = msg.senderType === 'customer' ? 'me' : 'them';
          } else if (msg.sender === 'customer') {
            sender = 'me';
          } else if (msg.sender === 'provider') {
            sender = 'them';
          }
          
          return {
            ...msg,
            sender: sender,
            timestamp: msg.timestamp || new Date().toISOString()
          };
        });
        
        setMessages(processedMessages);
        
        // Update chatInfo with provider info from response if available
        if (response.data.chat && response.data.chat.provider && !chatInfo?.actualChatId) {
          const provider = response.data.chat.provider;
          const updatedChatInfo = {
            id: chatId, // Use provider ID for display
            actualChatId: specificChatId, // Store actual chat ID for sending messages
            name: provider.businessName || `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || 'Provider',
            // Multiple image source options from chat response
            photoURL: provider.photoURL,
            profilePhotoUrl: provider.profilePhotoUrl,
            profilePicture: provider.profilePicture,
            logoUrl: provider.logoUrl,
            businessImage: provider.businessImage,
            image: provider.image,
            avatar: provider.logoUrl || provider.profilePhotoUrl, // Keep for backward compatibility
            isOnline: false,
            lastSeen: null,
            providerId: provider.id,
            businessType: provider.businessType
          };
          setChatInfo(updatedChatInfo);
        }
      } else {
        setError('Failed to load messages');
      }
    } catch (error) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Initialize WebSocket connection - ONLY ONCE
  useEffect(() => {
    const token = localStorage.getItem('customerToken') || localStorage.getItem('token');
    
    if (!token) {
      console.log('❌ No token available for WebSocket connection');
      return;
    }

    // Connect to WebSocket (will reuse existing connection if available)
      websocketService.connect(token);
      
    // Sync connection status immediately
      const currentStatus = websocketService.getConnectionStatus();
    setIsConnected(currentStatus);
      
      // Set up WebSocket event listeners
    const handleConnectionEstablished = (data) => {
      console.log('✅ Chat: WebSocket connected');
        setIsConnected(true);
    };

    const handleDisconnected = (reason) => {
      console.log('❌ Chat: WebSocket disconnected:', reason);
        setIsConnected(false);
    };

    const handleConnectionError = (error) => {
      console.error('❌ Chat: WebSocket connection error:', error);
        setIsConnected(false);
    };

    const handleNewMessage = (messageData) => {
        console.log('🔔 Customer received message via WebSocket:', messageData);
        console.log('🔔 Message sender:', messageData.sender);
        console.log('🔔 Message chatId:', messageData.chatId, typeof messageData.chatId);
        console.log('🔔 Current actualChatId:', chatInfoRef.current?.actualChatId, typeof chatInfoRef.current?.actualChatId);
        
        // Only process messages for the current chat (handle both string and number types)
        const messageChatId = messageData.chatId?.toString();
        const currentChatId = chatInfoRef.current?.actualChatId?.toString(); // ✅ Use actualChatId from ref
        
        if (!currentChatId || messageChatId !== currentChatId) {
          console.log('⚠️ Message not for current chat, ignoring');
          return;
        }
        
        // Add new message to the messages array
        const newMessage = {
          id: messageData.id,
          text: messageData.text || messageData.content,
          content: messageData.text || messageData.content,
          sender: messageData.sender, // Keep original sender ('me' or 'them')
          timestamp: new Date(messageData.timestamp),
          status: messageData.status || 'sent',
          type: messageData.type || 'text'
        };
        
        console.log('✅ Adding message to chat:', newMessage);
        
        setMessages(prev => {
          // Simple check - just add the message if it doesn't exist by ID
          const exists = prev.some(msg => msg.id === newMessage.id);
          if (exists) {
            console.log('⚠️ Message already exists, skipping');
            return prev;
          }
          console.log('✅ Message added to state');
          return [...prev, newMessage];
        });
    };

    const handleMessageSent = (messageData) => {
      console.log('🔔 Customer message sent confirmation:', messageData);
      
      // Only add if it's for this chat
      const messageChatId = messageData.chatId?.toString();
      const currentChatId = chatInfoRef.current?.actualChatId?.toString(); // ✅ Use actualChatId from ref
      
      if (!currentChatId || messageChatId !== currentChatId) {
        console.log('⚠️ Confirmation not for current chat, ignoring');
        return;
      }
      
      const realMessageId = messageData.id || messageData._id;
      const messageContent = messageData.text || messageData.content;
      
      setMessages(prev => {
        // Check if message with this real ID already exists
        const existsWithRealId = prev.some(msg => msg.id === realMessageId);
        if (existsWithRealId) {
          console.log('⚠️ Message with real ID already exists, updating status only');
          return prev.map(msg => 
            msg.id === realMessageId ? { ...msg, status: 'sent' } : msg
          );
        }
        
        // Check if there's an optimistic message with same content (sent in last 10 seconds)
        const recentOptimisticMsg = prev.find(msg => 
          (msg.content === messageContent || msg.text === messageContent) && 
          msg.sender === 'me' &&
          (Date.now() - new Date(msg.timestamp).getTime()) < 10000 && // Within last 10 seconds
          (msg.status === 'sending' || msg.status === 'sent')
        );
        
        if (recentOptimisticMsg) {
          console.log('✅ Updating optimistic message with real ID');
          return prev.map(msg => 
            msg.id === recentOptimisticMsg.id 
              ? { 
                  ...msg, 
                  id: realMessageId,
                  timestamp: messageData.timestamp || msg.timestamp,
                  status: 'sent'
                }
              : msg
          );
        }
        
        // No existing message, add as new
        console.log('✅ Adding new sent message to customer chat');
        const newMessage = {
          id: realMessageId,
          text: messageContent,
          content: messageContent,
          sender: 'me',
          timestamp: new Date(messageData.timestamp),
          status: messageData.status || 'sent',
          type: messageData.type || 'text'
        };
        return [...prev, newMessage];
      });
    };

    const handleTypingStatus = (data) => {
        // Handle typing indicator
      setIsTyping(data.isTyping);
    };

    const handleChatError = (error) => {
      console.error('❌ Chat error:', error);
        setError(error.message);
    };

    // Register all event listeners
    websocketService.on('connection_established', handleConnectionEstablished);
    websocketService.on('disconnected', handleDisconnected);
    websocketService.on('connection_error', handleConnectionError);
    websocketService.on('new_message', handleNewMessage);
    websocketService.on('message_sent', handleMessageSent);
    websocketService.on('typing_status', handleTypingStatus);
    websocketService.on('chat_error', handleChatError);

    return () => {
      // Clean up WebSocket listeners when component unmounts
      websocketService.off('connection_established', handleConnectionEstablished);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.off('connection_error', handleConnectionError);
      websocketService.off('new_message', handleNewMessage);
      websocketService.off('message_sent', handleMessageSent);
      websocketService.off('typing_status', handleTypingStatus);
      websocketService.off('chat_error', handleChatError);
      
      // Leave chat room on unmount
      if (chatInfoRef.current?.actualChatId) {
        websocketService.leaveChat(chatInfoRef.current.actualChatId);
      }
    };
  }, []); // ✅ Empty deps - handlers use ref to access latest chatInfo

  // Fetch messages when component mounts
  useEffect(() => {
    if (chatId) {
      // Check if we already have the actual chat ID from navigation state
      const actualChatId = location.state?.provider?.actualChatId;
      
      if (actualChatId) {
        // Set chatInfo from navigation state
        if (location.state.provider) {
          const provider = location.state.provider;
          setChatInfo({
            id: chatId, // Use provider ID for display
            actualChatId: actualChatId, // Store actual chat ID for API calls
            name: provider.name || 'Provider',
            // Multiple image source options from location state
            photoURL: provider.photoURL,
            profilePhotoUrl: provider.profilePhotoUrl,
            profilePicture: provider.profilePicture,
            logoUrl: provider.logoUrl,
            businessImage: provider.businessImage,
            image: provider.image,
            avatar: provider.avatar, // Keep for backward compatibility
            isOnline: false,
            lastSeen: null,
            providerId: provider.id,
            businessType: provider.businessType
          });
        }
        // Fetch messages for the existing chat
        fetchMessagesForChat(actualChatId);
      } else if (chatId.length === 24) { // MongoDB ObjectId length
        createOrFindChat(chatId);
      } else {
        // This is an actual chat ID, just fetch messages
        fetchMessages();
      }
    }
  }, [chatId]);

  // Mark messages as read when chat is viewed
  useEffect(() => {
    const markMessagesAsRead = async () => {
      if (chatInfo?.actualChatId && messages.length > 0) {
        try {
          // Get unread message IDs (messages from provider that haven't been read)
          const unreadMessageIds = messages
            .filter(msg => msg.senderType === 'provider' && !msg.isRead)
            .map(msg => msg.id);
          
          if (unreadMessageIds.length > 0) {
            await chatAPI.markAsRead(chatInfo.actualChatId, unreadMessageIds);
            
            // Refresh the chat count in the header
            if (window.refreshChatCount) {
              window.refreshChatCount();
            }
          }
        } catch (error) {
        }
      }
    };

    // Mark as read after a short delay to ensure messages are loaded
    const timer = setTimeout(markMessagesAsRead, 1000);
    return () => clearTimeout(timer);
  }, [chatInfo?.actualChatId, messages]);

  // Simple approach - no complex tracking needed

  // Join chat room when connected and actualChatId is available
  useEffect(() => {
    if (isConnected && chatInfoRef.current?.actualChatId) {
      console.log('💬 Customer joining chat room:', chatInfoRef.current.actualChatId);
      websocketService.joinChat(chatInfoRef.current.actualChatId.toString());
      
      // Return cleanup to leave previous chat room when chatInfo changes
      return () => {
        if (chatInfoRef.current?.actualChatId) {
          console.log('💬 Customer leaving chat room:', chatInfoRef.current.actualChatId);
          websocketService.leaveChat(chatInfoRef.current.actualChatId.toString());
        }
      };
    }
  }, [isConnected, chatInfo]); // ✅ Re-join when chatInfo changes (actualChatId populated)

  // Periodic connection status sync - ONLY check status, don't reconnect
        useEffect(() => {
          const syncInterval = setInterval(() => {
            const wsConnected = websocketService.getConnectionStatus();
            if (wsConnected !== isConnected) {
              setIsConnected(wsConnected);
            }
    }, 5000); // Check every 5 seconds (less aggressive)

          return () => clearInterval(syncInterval);
        }, [isConnected]);

  // Create or find chat with provider
  const createOrFindChat = async (providerId) => {
    try {
      setLoading(true);
      setError(null);
      
      // First try to create or find existing chat
      const response = await chatAPI.createChat(providerId);
      
      if (response.success) {
        // Get provider information from the response
        const actualChatId = response.data.chatId;
        const provider = response.data.provider;
        
        if (actualChatId) {
          // Set provider information if available
          if (provider) {
            const newChatInfo = {
              id: providerId, // Use provider ID for display
              actualChatId: actualChatId, // Store actual chat ID for API calls
              name: provider.businessName || `${provider.firstName} ${provider.lastName}`,
              // Multiple image source options from createChat response
              photoURL: provider.photoURL,
              profilePhotoUrl: provider.profilePhotoUrl,
              profilePicture: provider.profilePicture,
              logoUrl: provider.logoUrl,
              businessImage: provider.businessImage,
              image: provider.image,
              avatar: provider.logoUrl || provider.profilePhotoUrl, // Keep for backward compatibility
              isOnline: false,
              lastSeen: null,
              providerId: provider.id,
              businessType: provider.businessType
            };
            setChatInfo(newChatInfo);
          } else {
            // Fallback: fetch provider info separately
            await fetchProviderInfo(providerId);
          }
          
          // Now fetch messages for this chat
          await fetchMessagesForChat(actualChatId);
        } else {
          setError('Failed to get chat ID from server');
        }
      } else {
        setError('Failed to create chat conversation');
      }
    } catch (error) {
      setError('Failed to create chat conversation');
    } finally {
      setLoading(false);
    }
  };

  // Fetch provider information
  const fetchProviderInfo = async (providerId) => {
    try {
      const response = await chatAPI.getProviderInfo(providerId);
      if (response.success) {
        const provider = response.data;
        setChatInfo({
          id: providerId,
          name: provider.businessName || `${provider.firstName} ${provider.lastName}`,
          // Multiple image source options from provider API
          photoURL: provider.photoURL,
          profilePhotoUrl: provider.profilePhotoUrl,
          profilePicture: provider.profilePicture,
          logoUrl: provider.logoUrl,
          businessImage: provider.businessImage,
          image: provider.image,
          avatar: provider.logoUrl || provider.profilePhotoUrl, // Keep for backward compatibility
          isOnline: false, // TODO: Implement online status
          lastSeen: null
        });
      } else {
      }
    } catch (error) {
      // Set default provider info
      setChatInfo({
        id: providerId,
        name: 'Provider',
        photoURL: null,
        profilePhotoUrl: null,
        profilePicture: null,
        logoUrl: null,
        businessImage: null,
        image: null,
        avatar: null,
        isOnline: false,
        lastSeen: null
      });
    }
  };

  // Fetch provider information from chat data
  const fetchProviderInfoFromChat = async (chatId) => {
    try {
      const response = await chatAPI.getChatMessages(chatId);
      if (response.success && response.data.provider) {
        const provider = response.data.provider;
        setChatInfo({
          id: provider.id,
          name: provider.businessName || `${provider.firstName} ${provider.lastName}`,
          // Multiple image source options from chat data
          photoURL: provider.photoURL,
          profilePhotoUrl: provider.profilePhotoUrl,
          profilePicture: provider.profilePicture,
          logoUrl: provider.logoUrl,
          businessImage: provider.businessImage,
          image: provider.image,
          avatar: provider.logoUrl || provider.profilePhotoUrl, // Keep for backward compatibility
          isOnline: false,
          lastSeen: null
        });
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Scroll to search result when it changes
  useEffect(() => {
    if (searchResults.length > 0 && currentSearchIndex >= 0) {
      const currentResult = searchResults[currentSearchIndex];
      const messageElement = document.querySelector(`[data-message-id="${currentResult.id}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }, [currentSearchIndex, searchResults]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '--:--';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const today = new Date();
    const messageDate = new Date(timestamp);
    
    if (isNaN(messageDate.getTime())) return 'Invalid Date';
    
    if (messageDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (messageDate.toDateString() === new Date(today.getTime() - 24 * 60 * 60 * 1000).toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString();
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() && !isSending) {
      setIsSending(true);
      const messageText = message.trim();
      const tempId = Date.now(); // Temporary ID for optimistic update
      setMessage(''); // Clear input immediately

      // Add message optimistically to UI immediately
      const optimisticMessage = {
        id: tempId,
        content: messageText,
        sender: 'me',
        timestamp: new Date().toISOString(),
        status: 'sending',
        type: 'text'
      };
      
      setMessages(prev => [...prev, optimisticMessage]);
      console.log('📤 Adding optimistic message to UI:', optimisticMessage);

      try {
        // Use actualChatId (NOT provider ID from URL)
        const messageChatId = chatInfo?.actualChatId?.toString();
        
        if (!messageChatId) {
          console.error('❌ No actual chat ID available');
          setError('Chat not initialized');
          setMessages(prev => prev.map(msg => 
            msg.id === tempId ? { ...msg, status: 'failed' } : msg
          ));
          setIsSending(false);
          return;
        }
        
        // Send message via API
        const response = await chatAPI.sendMessage(messageChatId, {
          content: messageText,
          messageType: 'text'
        });
        
        if (response.success) {
          console.log('✅ Customer message sent successfully via API:', response);
          
          // Update the optimistic message with real data
          setMessages(prev => prev.map(msg => 
            msg.id === tempId 
              ? { ...msg, id: response.data?.id || response.data?._id || tempId, status: 'sent' }
              : msg
          ));
          setIsSending(false);
        } else {
          setError('Failed to send message');
          // Mark message as failed
          setMessages(prev => prev.map(msg => 
            msg.id === tempId 
              ? { ...msg, status: 'failed' }
              : msg
          ));
          setIsSending(false);
        }
      } catch (error) {
        console.error('❌ Error sending message:', error);
        setError('Failed to send message');
        // Mark message as failed
        setMessages(prev => prev.map(msg => 
          msg.id === tempId 
            ? { ...msg, status: 'failed' }
            : msg
        ));
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSending) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (fileType) => {
    setFileDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.accept = fileType;
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const newMessage = {
        id: Date.now(),
        file: file,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        sender: 'me',
        timestamp: new Date(),
        status: 'sent',
        type: 'file'
      };
      setMessages(prev => [...prev, newMessage]);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <Image />;
    if (fileType === 'application/pdf') return <PictureAsPdf />;
    if (fileType.startsWith('video/')) return <Videocam />;
    return <Description />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Enhanced status tracking functions
  const updateMessageStatus = (messageId, status) => {
    setMessageStatuses(prev => ({
      ...prev,
      [messageId]: status
    }));
  };

  const simulateStatusUpdates = (messageId) => {
    // Simulate delivery after 1 second
    setTimeout(() => {
      updateMessageStatus(messageId, 'delivered');
    }, 1000);
    
    // Simulate read after 3 seconds
    setTimeout(() => {
      updateMessageStatus(messageId, 'read');
    }, 3000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Check sx={{ fontSize: 16, color: '#8696A0' }} />;
      case 'delivered':
        return <Done sx={{ fontSize: 16, color: '#8696A0' }} />;
      case 'read':
        return <DoneAll sx={{ fontSize: 16, color: '#34B7F1' }} />;
      case 'failed':
        return <Close sx={{ fontSize: 16, color: '#FF4444' }} />;
      default:
        return null;
    }
  };

  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      const results = messages.filter(msg => 
        (msg.text || msg.content || '').toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
      setCurrentSearchIndex(0);
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(0);
    }
  };

  const handleSearchNext = () => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : 0
      );
    }
  };

  const handleSearchPrev = () => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex(prev => 
        prev > 0 ? prev - 1 : searchResults.length - 1
      );
    }
  };

  const handleExitSearch = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
  };

  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      // Ensure proper spacing for mobile safe areas
      pt: isMobile ? 'env(safe-area-inset-top, 0px)' : 0,
      pb: isMobile ? 'env(safe-area-inset-bottom, 0px)' : 0
    }}>
      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}>
          <BrandLoader />
        </Box>
      )}


      {/* Main Content - Always show, with error notification if needed */}
      {!loading && (
        <>
          {/* Error Notification - Only show if there's a real error, not during loading */}
          {error && error !== 'Chat not found' && (
            <Box sx={{ p: 2, backgroundColor: '#ffebee' }}>
              <Alert 
                severity="error" 
                sx={{ mb: 1 }}
                action={
            <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => setError(null)}
                  >
                    ✕
            </Button>
                }
              >
                {error}
          </Alert>
        </Box>
      )}

          {/* Header */}
          <AppBar 
            position="static" 
            sx={{ 
              backgroundColor: '#0A2A66',
              flexShrink: 0
            }}
          >
        <Toolbar sx={{
          minHeight: isMobile ? 56 : 64,
          px: isMobile ? 1 : 2
        }}>
          {!isSearchMode ? (
            <>
              <IconButton 
                edge="start" 
                color="inherit" 
                onClick={() => navigate('/chat')}
                sx={{ mr: 2 }}
              >
                <ArrowBack />
              </IconButton>
              
              <Avatar 
                src={chatInfo.photoURL || chatInfo.profilePhotoUrl || chatInfo.profilePicture || chatInfo.logoUrl || chatInfo.businessImage || chatInfo.image || chatInfo.avatar || ''} 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  mr: 2,
                  border: '2px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {/* Fallback: Show first letter of business name if no image */}
                {!chatInfo.photoURL && !chatInfo.profilePhotoUrl && !chatInfo.profilePicture && !chatInfo.logoUrl && !chatInfo.businessImage && !chatInfo.image && !chatInfo.avatar && chatInfo.name ? 
                  chatInfo.name.charAt(0).toUpperCase() : null
                }
              </Avatar>
              
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {chatInfo.name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
                </Typography>
              </Box>
              
              
              <IconButton 
                color="inherit" 
                size="small"
                onClick={() => setIsSearchMode(true)}
                sx={{ mr: 1 }}
              >
                <Search />
              </IconButton>
              
              <IconButton 
                color="inherit" 
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <MoreVert />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton 
                edge="start" 
                color="inherit" 
                onClick={handleExitSearch}
                sx={{ mr: 2 }}
              >
                <ArrowBack />
              </IconButton>
              
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <InputBase
                  ref={searchInputRef}
                  placeholder="Search in conversation..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  sx={{ 
                    color: 'white',
                    '& .MuiInputBase-input': {
                      color: 'white',
                      '&::placeholder': {
                        color: 'rgba(255,255,255,0.7)',
                        opacity: 1
                      }
                    }
                  }}
                  autoFocus
                />
              </Box>
              
              {searchResults.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  <Typography variant="caption" sx={{ color: 'white', mr: 1 }}>
                    {currentSearchIndex + 1} of {searchResults.length}
                  </Typography>
                  <IconButton 
                    color="inherit" 
                    size="small"
                    onClick={handleSearchPrev}
                  >
                    <ArrowBack sx={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                  <IconButton 
                    color="inherit" 
                    size="small"
                    onClick={handleSearchNext}
                  >
                    <ArrowBack sx={{ transform: 'rotate(-90deg)' }} />
                  </IconButton>
                </Box>
              )}
              
              <IconButton 
                color="inherit" 
                size="small"
                onClick={handleExitSearch}
              >
                <Close />
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Messages Area */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        backgroundColor: '#E5DDD5',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="0.1"/%3E%3C/svg%3E")',
        minHeight: 0,
        WebkitOverflowScrolling: 'touch'
      }}>
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <Box key={date}>
            {/* Date Separator */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              my: 2 
            }}>
              <Chip 
                label={date} 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(0,0,0,0.1)', 
                  color: 'rgba(0,0,0,0.7)',
                  fontSize: '0.75rem'
                }} 
              />
            </Box>
            
            {/* Messages */}
            {dateMessages.map((msg) => {
              const isSearchResult = searchResults.some(result => result.id === msg.id);
              const isCurrentSearchResult = searchResults[currentSearchIndex]?.id === msg.id;
              
              return (
                <Box
                  key={msg.id}
                  data-message-id={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.sender === 'me' ? 'flex-end' : 'flex-start',
                    mb: 1,
                    px: 2
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '70%',
                      backgroundColor: msg.sender === 'me' ? '#DCF8C6' : 'white',
                      borderRadius: 2,
                      p: 1.5,
                      position: 'relative',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      border: isCurrentSearchResult ? '2px solid #34B7F1' : 'none',
                      backgroundColor: isSearchResult 
                        ? (msg.sender === 'me' ? '#E8F5E8' : '#F0F8FF') 
                        : (msg.sender === 'me' ? '#DCF8C6' : 'white')
                    }}
                  >
                    {msg.type === 'text' ? (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          wordBreak: 'break-word',
                          backgroundColor: isCurrentSearchResult ? '#FFEB3B' : 'transparent',
                          padding: isCurrentSearchResult ? '2px 4px' : 0,
                          borderRadius: isCurrentSearchResult ? '4px' : 0
                        }}
                      >
                        {msg.text || msg.content || 'No message content'}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileIcon(msg.fileType)}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {msg.fileName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(msg.fileSize)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      mt: 0.5
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {formatTime(msg.timestamp)}
                      </Typography>
                      {msg.sender === 'me' && getStatusIcon(messageStatuses[msg.id] || msg.status)}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
        
        {/* Typing Indicator */}
        {isTyping && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1, px: 2 }}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#999',
                    animation: 'typing 1.4s infinite ease-in-out'
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#999',
                    animation: 'typing 1.4s infinite ease-in-out 0.2s'
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#999',
                    animation: 'typing 1.4s infinite ease-in-out 0.4s'
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={{ 
        backgroundColor: '#f0f0f0', 
        p: 2,
        pb: isMobile ? 2 : 2,
        borderTop: '1px solid #e0e0e0',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            size="small"
            onClick={() => setFileDialogOpen(true)}
            sx={{ color: '#075E54' }}
          >
            <AttachFile />
          </IconButton>
          
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type a message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            multiline
            maxRows={4}
            size="small"
            sx={{
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
              }
            }}
          />
          
          <IconButton 
            size="small"
            sx={{ color: '#075E54' }}
          >
            <EmojiEmotions />
          </IconButton>
          
          <IconButton 
            size="small"
            onClick={handleSendMessage}
            disabled={!message.trim() || isSending}
            sx={{ 
              color: (message.trim() && !isSending) ? '#075E54' : '#ccc',
              backgroundColor: (message.trim() && !isSending) ? '#DCF8C6' : '#f5f5f5'
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </Box>

      {/* File Upload Dialog */}
      <Dialog open={fileDialogOpen} onClose={() => setFileDialogOpen(false)}>
        <DialogTitle>Send File</DialogTitle>
        <DialogContent>
          <List>
            <ListItem button onClick={() => handleFileSelect('image/*')}>
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: '#25D366' }}>
                  <Image />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="Photo" secondary="Send an image" />
            </ListItem>
            <ListItem button onClick={() => handleFileSelect('video/*')}>
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: '#25D366' }}>
                  <Videocam />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="Video" secondary="Send a video" />
            </ListItem>
            <ListItem button onClick={() => handleFileSelect('application/pdf')}>
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: '#25D366' }}>
                  <PictureAsPdf />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="Document" secondary="Send a PDF or document" />
            </ListItem>
            <ListItem button onClick={() => handleFileSelect('*/*')}>
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: '#25D366' }}>
                  <Description />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="File" secondary="Send any file" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setAnchorEl(null);
          setIsSearchMode(true);
        }}>
          <Search sx={{ mr: 2 }} />
          Search in conversation
        </MenuItem>
      </Menu>

      {/* Typing Animation */}
      <style>
        {`
          @keyframes typing {
            0%, 60%, 100% {
              transform: translateY(0);
            }
            30% {
              transform: translateY(-10px);
            }
          }
        `}
      </style>
        </>
      )}
    </Box>
  );
};

export default ChatConversation;
